import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  X, 
  Building2, 
  Users, 
  FileText, 
  Shield, 
  BookOpen,
  ArrowRight,
  CreditCard,
  Check,
  AlertCircle,
  Mail,
  Instagram,
  RefreshCw
} from 'lucide-react';
import { createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config';
import { createUserProfile } from '../firebase/collections';
import { useRealtime } from '../contexts/RealtimeContext';
import SmartPaymentModal from '../components/payment/SmartPaymentModal';
import SubscriptionPaymentModal from '../components/payment/SubscriptionPaymentModal';
import EducationalSupportProgram from '../components/common/EducationalSupportProgram';
import WhatsAppButton from '../components/common/WhatsAppButton';
import SEOHead from '../components/SEOHead';
import toast from 'react-hot-toast';
import { getSubscriptionPlans, CATEGORIES, STUDENT_RANGES, getBaseBatchLimit, BATCH_ADDON_CONFIG } from '../utils/subscriptionPricing';

const CoachingSignup = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshUserProfile } = useRealtime();
  const [currentStep, setCurrentStep] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [enableAutopay, setEnableAutopay] = useState(false);
  const [formData, setFormData] = useState({
    coachingName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    category: '',
    studentRange: '',
    password: '',
    confirmPassword: '',
    agreedToTerms: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const features = [
    {
      icon: FileText,
      title: 'Test Management',
      description: 'Create and manage unlimited mock tests and practice papers for your students'
    },
    {
      icon: Shield,
      title: 'Exam Management',
      description: 'Organize and manage multiple exams with detailed analytics and reporting'
    },
    {
      icon: BookOpen,
      title: 'Resource Management',
      description: 'Upload and organize study materials, notes, and resources for your students'
    },
    {
      icon: Users,
      title: 'Batch Management',
      description: 'Create and manage student batches with class schedules and enrollments'
    },
    {
      icon: Building2,
      title: 'Action Centre',
      description: 'Centralized dashboard for quick actions, notifications, and task management'
    },
    {
      icon: Users,
      title: 'Teacher Management',
      description: 'Manage teachers, assign subjects, track attendance, and monitor performance'
    },
    {
      icon: FileText,
      title: 'Report Cards',
      description: 'Generate comprehensive report cards with grades, remarks, and performance analytics'
    },
    {
      icon: CreditCard,
      title: 'Fees Management',
      description: 'Complete fee collection, tracking, reminders, and payment history management'
    },
    {
      icon: CheckCircle,
      title: 'And Many More...',
      description: 'Attendance, timetable, transport, library, ID cards, SMS/WhatsApp notifications & more'
    }
  ];

  const benefits = [
    'Complete data isolation - Your students\' data is completely private',
    'Unlimited tests, resources, and batches',
    'Real-time analytics and performance tracking',
    'Easy student enrollment and batch management',
    'Secure payment processing',
    'Regular platform updates and new features'
  ];

  // Get dynamic subscription plans based on category and student range
  const subscriptionPlans = formData.category && formData.studentRange
    ? getSubscriptionPlans(formData.category, formData.studentRange)
    : getSubscriptionPlans('coaching', '0-500'); // Default fallback

  // Get batch limit for selected category and student range
  const baseBatchLimit = formData.category && formData.studentRange
    ? getBaseBatchLimit(formData.category, formData.studentRange)
    : 20;

  const [selectedPlan, setSelectedPlan] = useState('yearly');

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.coachingName.trim()) newErrors.coachingName = 'Coaching name is required';
    if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact person name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number (10 digits required)';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^[0-9]{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Invalid pincode (6 digits required)';
    }
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.studentRange) newErrors.studentRange = 'Student range is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the terms and conditions';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSignup = async () => {
    if (!validateStep2()) return;

    setLoading(true);
    try {
      let user;
      let isExistingUser = false;

      // Check if user is already logged in
      if (auth.currentUser) {
        user = auth.currentUser;
        isExistingUser = true;
        toast('Using existing account', { icon: 'ℹ️' });
      } else {
        // Create user account
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        user = userCredential.user;
      }

      // Update user profile
      if (!isExistingUser) {
        await updateProfile(user, {
          displayName: formData.contactPerson
        });
      }

      // Check if profile already exists
      const { getUserProfile, updateUserProfile } = await import('../firebase/collections');
      const existingProfile = await getUserProfile(user.uid);

      // Generate coaching ID (use existing if available)
      const coachingId = existingProfile.success && existingProfile.data?.coachingId 
        ? existingProfile.data.coachingId 
        : `coaching_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      if (existingProfile.success) {
        // Update existing profile - convert to coaching admin
        // Use setDoc to ensure role is properly updated
        const { doc, setDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase/config');
        const { COLLECTIONS } = await import('../firebase/collections');
        
        // Merge existing data with new coaching admin data
        await setDoc(doc(db, COLLECTIONS.USERS, user.uid), {
          ...existingProfile.data, // Keep existing data
          role: 'coaching_admin', // Override role
          coachingId: coachingId,
          coachingName: formData.coachingName,
          contactPerson: formData.contactPerson,
          phone: formData.phone,
          category: formData.category,
          studentRange: formData.studentRange,
          address: {
            street: formData.address,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode
          },
          subscription: {
            plan: selectedPlan,
            status: 'pending',
            startDate: null,
            endDate: null
          },
          isActive: false,
          profileCompleted: true,
          updatedAt: new Date()
        }, { merge: true }); // Use merge: true to preserve other fields
        
        toast('Account updated with coaching information', { icon: 'ℹ️' });
      } else {
        // Create new profile
        const userProfileData = {
          uid: user.uid,
          email: user.email,
          displayName: formData.contactPerson,
          name: formData.contactPerson,
          role: 'coaching_admin',
          coachingId: coachingId,
          coachingName: formData.coachingName,
          contactPerson: formData.contactPerson,
          phone: formData.phone,
          category: formData.category,
          studentRange: formData.studentRange,
          address: {
            street: formData.address,
            city: formData.city,
            state: formData.state,
            pincode: formData.pincode
          },
          subscription: {
            plan: selectedPlan,
            status: 'pending',
            startDate: null,
            endDate: null
          },
          isActive: false, // Will be activated after payment
          profileCompleted: true,
          createdAt: new Date(),
          updatedAt: new Date()
        };

        await createUserProfile(userProfileData);
      }

      // Show payment modal for one-time payment
      if (enableAutopay) {
        setShowSubscriptionModal(true);
      } else {
        setShowPaymentModal(true);
      }
      setLoading(false); // Stop loading so modal can show
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        toast.error('This email is already registered. Please login to continue.');
      } else {
        toast.error(error.message || 'Failed to create account');
      }
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (response, transactionData) => {
    try {
      // Update user profile to activate subscription
      const user = auth.currentUser;
      if (user) {
        const { updateUserProfile } = await import('../firebase/collections');
        const plan = subscriptionPlans.find(p => p.id === selectedPlan);
        const endDate = new Date();
        if (selectedPlan === 'monthly') {
          endDate.setMonth(endDate.getMonth() + 1);
        } else if (selectedPlan === 'quarterly') {
          endDate.setMonth(endDate.getMonth() + 3);
        } else if (selectedPlan === 'yearly') {
          endDate.setFullYear(endDate.getFullYear() + 1);
        }

        await updateUserProfile(user.uid, {
          'subscription.plan': selectedPlan,
          'subscription.status': 'active',
          'subscription.startDate': new Date(),
          'subscription.endDate': endDate,
          'subscription.transactionId': transactionData?.paymentId || transactionData?.transactionId,
          'subscription.orderId': transactionData?.orderId,
          isActive: true,
          updatedAt: new Date()
        });

        // Save payment record
        const { paymentManagementService } = await import('../firebase/paymentManagement');
        const { getUserProfile } = await import('../firebase/collections');
        const profileResult = await getUserProfile(user.uid);
        const coachingId = profileResult.success && profileResult.data?.coachingId
          ? profileResult.data.coachingId
          : `coaching_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        await paymentManagementService.saveCoachingSubscriptionPayment({
          coachingName: formData.coachingName,
          plan: selectedPlan,
          planName: plan?.name || 'Monthly Plan',
          coachingId: coachingId,
          transactionId: transactionData?.paymentId || transactionData?.transactionId,
          orderId: transactionData?.orderId,
          paymentStatus: 'completed',
          paymentAmount: plan?.price || 1000,
          paymentDate: new Date().toISOString(),
          userId: user.uid,
          customerEmail: formData.email,
          customerName: formData.contactPerson,
          actionType: 'signup'
        });

        toast.success('Payment successful! Your coaching account is now active.');
        
        // Close modal immediately
        setShowPaymentModal(false);
        
        // Navigate after a short delay
        setTimeout(() => {
          navigate('/coaching-admin');
        }, 1000);
      }
    } catch (error) {
      toast.error('Account created but payment verification failed. Please contact support.');
      setShowPaymentModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionSuccess = async (response, transactionData) => {
    try {
      // Update user profile to activate subscription with autopay
      const user = auth.currentUser;
      if (user) {
        const { updateUserProfile } = await import('../firebase/collections');
        const plan = subscriptionPlans.find(p => p.id === selectedPlan);
        const endDate = new Date();
        if (selectedPlan === 'monthly') {
          endDate.setMonth(endDate.getMonth() + 1);
        } else if (selectedPlan === 'quarterly') {
          endDate.setMonth(endDate.getMonth() + 3);
        } else if (selectedPlan === 'yearly') {
          endDate.setFullYear(endDate.getFullYear() + 1);
        }

        await updateUserProfile(user.uid, {
          'subscription.plan': selectedPlan,
          'subscription.status': 'active',
          'subscription.startDate': new Date(),
          'subscription.endDate': endDate,
          'subscription.razorpaySubscriptionId': transactionData?.subscriptionId,
          'subscription.transactionId': transactionData?.paymentId || transactionData?.transactionId,
          'subscription.orderId': transactionData?.orderId,
          'subscription.autopayEnabled': true,
          isActive: true,
          updatedAt: new Date()
        });

        // Save payment record
        const { paymentManagementService } = await import('../firebase/paymentManagement');
        const { getUserProfile } = await import('../firebase/collections');
        const profileResult = await getUserProfile(user.uid);
        const coachingId = profileResult.success && profileResult.data?.coachingId
          ? profileResult.data.coachingId
          : `coaching_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        await paymentManagementService.saveCoachingSubscriptionPayment({
          coachingName: formData.coachingName,
          plan: selectedPlan,
          planName: plan?.name || 'Monthly Plan',
          coachingId: coachingId,
          razorpaySubscriptionId: transactionData?.subscriptionId,
          transactionId: transactionData?.paymentId || transactionData?.transactionId,
          orderId: transactionData?.orderId,
          paymentStatus: 'completed',
          paymentAmount: plan?.price || 1000,
          paymentDate: new Date().toISOString(),
          userId: user.uid,
          customerEmail: formData.email,
          customerName: formData.contactPerson,
          actionType: 'signup_autopay'
        });

        toast.success('Autopay subscription activated! Your coaching account is now active.');
        
        // Close modal immediately
        setShowSubscriptionModal(false);
        
        // Navigate after a short delay
        setTimeout(() => {
          navigate('/coaching-admin');
        }, 1000);
      }
    } catch (error) {
      toast.error('Account created but subscription activation failed. Please contact support.');
      setShowSubscriptionModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentFailure = (error) => {
    toast.error('Payment failed. Please try again.');
    setLoading(false);
    setShowPaymentModal(false);
  };



  const selectedPlanData = subscriptionPlans.find(p => p.id === selectedPlan);

  // SEO Data for School ERP / Coaching Software page
  const seoData = {
    title: "Register School ERP | Best Coaching Software & Institute Management System - MahStry",
    description: "Join MahStry - India's #1 School ERP & Coaching Institute Software. Get complete school management system with student management, fee collection, attendance, timetable, batch management, online classes, and more. Start free trial today!",
    keywords: "school ERP registration, coaching software signup, institute management system, school management software, college ERP, coaching center software, student management system, fee management software, attendance software, timetable management, batch management software, online classes platform, school administration software, best school ERP India, free school software, coaching institute management, tuition management software, education ERP, school digitization, smart school software"
  };

  // Structured data for the signup page
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Register for School ERP & Coaching Software",
      "description": seoData.description,
      "url": "https://mahstry.com/coaching-signup",
      "mainEntity": {
        "@type": "SoftwareApplication",
        "name": "MahStry School ERP",
        "applicationCategory": "EducationalApplication",
        "operatingSystem": "Web, Android, iOS",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR",
          "description": "Free trial available"
        }
      }
    }
  ];

  return (
    <>
      <SEOHead 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical="https://mahstry.com/coaching-signup"
        structuredData={structuredData}
      />
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4"
          >
            Join as <span className="text-gradient">School/Coaching</span>
          </motion.h1>
          <p className="text-xl text-gray-600 dark:text-gray-400">
            Get your own isolated admin panel with complete control
          </p>
          
          {/* Contact Information for Onboarding Help */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-sm text-gray-600 dark:text-gray-400"
          >
            <span className="text-center sm:text-left">For any query or help regarding onboarding, contact us:</span>
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/mahstry"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 transition-colors"
                aria-label="Contact us on Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href="mailto:support@mahstry.com"
                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                aria-label="Email us at support@mahstry.com"
              >
                <Mail size={20} />
                <span>support@mahstry.com</span>
              </a>
            </div>
          </motion.div>
          
          {/* Show notice if user is already logged in */}
          {currentUser && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-2xl mx-auto"
            >
              <div className="flex items-start">
                <AlertCircle className="text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <div className="text-left">
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                    Logged in as: {currentUser.email}
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {userProfile?.role === 'coaching_admin' 
                      ? 'Your account will be updated with coaching information.'
                      : 'Your account will be converted to a coaching admin account. Fill the form below and complete payment to activate.'}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {currentStep > step ? <CheckCircle size={20} /> : step}
                </div>
                {step < 3 && (
                  <div
                    className={`w-16 h-1 ${
                      currentStep > step ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 md:p-8"
            >
              {/* Step 1: Basic Information */}
              {currentStep === 1 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Basic Information
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        School/Coaching Institute Name *
                      </label>
                      <input
                        type="text"
                        value={formData.coachingName}
                        onChange={(e) => handleInputChange('coachingName', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.coachingName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter your school/coaching institute name"
                      />
                      {errors.coachingName && (
                        <p className="mt-1 text-sm text-red-600">{errors.coachingName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Contact Person Name *
                      </label>
                      <input
                        type="text"
                        value={formData.contactPerson}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.contactPerson ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter contact person name"
                      />
                      {errors.contactPerson && (
                        <p className="mt-1 text-sm text-red-600">{errors.contactPerson}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter email address"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="10-digit phone number"
                        maxLength={10}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Address *
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        rows={3}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.address ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Enter full address"
                      />
                      {errors.address && (
                        <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                            errors.city ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="City"
                        />
                        {errors.city && (
                          <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          State *
                        </label>
                        <input
                          type="text"
                          value={formData.state}
                          onChange={(e) => handleInputChange('state', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                            errors.state ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="State"
                        />
                        {errors.state && (
                          <p className="mt-1 text-sm text-red-600">{errors.state}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Pincode *
                        </label>
                        <input
                          type="text"
                          value={formData.pincode}
                          onChange={(e) => handleInputChange('pincode', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                            errors.pincode ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="6-digit pincode"
                          maxLength={6}
                        />
                        {errors.pincode && (
                          <p className="mt-1 text-sm text-red-600">{errors.pincode}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category * 
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.category ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <option value="">Select category</option>
                        {CATEGORIES.map((cat) => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                      {errors.category && (
                        <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Number of Students * 
                      </label>
                      <select
                        value={formData.studentRange}
                        onChange={(e) => handleInputChange('studentRange', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.studentRange ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        <option value="">Select student range</option>
                        {STUDENT_RANGES.map((range) => (
                          <option key={range.value} value={range.value}>
                            {range.label}
                          </option>
                        ))}
                      </select>
                      {errors.studentRange && (
                        <p className="mt-1 text-sm text-red-600">{errors.studentRange}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Account Setup */}
              {currentStep === 2 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Account Setup
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Password *
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Minimum 8 characters"
                      />
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Confirm Password *
                      </label>
                      <input
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Re-enter password"
                      />
                      {errors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                      )}
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                        Terms & Conditions for Institutions
                      </h3>
                      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-3 mb-4 max-h-96 overflow-y-auto pr-2">
                        <div>
                          <p className="font-semibold mb-1">1. Platform Services & Benefits:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>Complete data isolation - your students' data will not be visible to other schools/coaching institutes</li>
                            <li>Access to MahStry's admission management system for handling applications and student admissions</li>
                            <li>Platform support for managing curriculum, materials, faculty information, and institution profile</li>
                            <li>Automated application processing and payment collection system</li>
                            <li>Refund and application dispute resolution handled by MahStry</li>
                            <li>Marketing and visibility to prospective students through our platform</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">2. Application Fee & Commission:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>MahStry will deduct 10% of the application fee from each admission application received through our platform</li>
                            <li>The remaining 90% will be transferred to your account as per the settlement schedule</li>
                            <li>All payment processing, refunds, and financial transactions will be handled by MahStry</li>
                            <li>For any payment or refund related issues, please contact us at <a href="mailto:support@mahstry.com" className="text-blue-600 underline">support@mahstry.com</a></li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">3. Refund & Application Management:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>MahStry will handle all refund requests and application-related disputes on your behalf</li>
                            <li>For any issues, concerns, or disputes regarding applications or refunds, please contact us at <a href="mailto:support@mahstry.com" className="text-blue-600 underline">support@mahstry.com</a></li>
                            <li>We will process refunds according to our refund policy and applicable regulations</li>
                            <li>All communication regarding applications and refunds should be directed to MahStry support</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">4. Subscription & Account Status:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>You must maintain an active subscription to keep your institution profile visible to students</li>
                            <li>If your subscription becomes inactive, MahStry reserves the right to temporarily hide your institution profile from public view</li>
                            <li>If your account remains inactive (no active subscription) for a continuous period of one (1) month, MahStry will permanently delete your institution profile and account from the platform</li>
                            <li>Upon account deletion, all your data including applications, curriculum, materials, and profile information will be permanently removed and cannot be recovered</li>
                            <li>Subscription fees are non-refundable once payment is processed</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">5. Student Admission & Rejection Policy:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>If you have admitted any student through the MahStry platform, you cannot reject or terminate their admission on any grounds without the explicit written consent of MahStry</li>
                            <li>Any decision to reject an already admitted student must be communicated to MahStry in writing at <a href="mailto:support@mahstry.com" className="text-blue-600 underline">support@mahstry.com</a> and will be subject to review</li>
                            <li>Unauthorized rejection of admitted students may result in immediate account suspension and legal action</li>
                            <li>You are responsible for honoring all commitments made to students admitted through our platform</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">6. Code of Conduct & Prohibited Activities:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>Any malicious activities, irregularities, or fraudulent behavior with applicants or students will not be tolerated</li>
                            <li>Institutions must foster scientific temper—evidence-based teaching without superstition or misinformation—and ensure zero discrimination, bias, hate speech, or harassment on any ground</li>
                            <li>Any form of bad behavior, harassment, discrimination, or unethical practices will result in immediate account termination</li>
                            <li>MahStry reserves the right to take legal action against institutions found engaging in fraudulent activities, misrepresentation, or any form of misconduct</li>
                            <li>You must comply with all applicable laws and regulations regarding student data privacy, education standards, and consumer protection</li>
                            <li>All content uploaded must comply with copyright laws and educational standards</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">7. Account Security & Responsibilities:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>You are responsible for maintaining the security of your account credentials</li>
                            <li>You must not share your account access with unauthorized persons</li>
                            <li>You are responsible for all activities that occur under your account</li>
                            <li>Immediately notify MahStry at <a href="mailto:support@mahstry.com" className="text-blue-600 underline">support@mahstry.com</a> if you suspect any unauthorized access to your account</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">8. Support & Contact:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>For any queries, issues, or support, please contact us at <a href="mailto:support@mahstry.com" className="text-blue-600 underline">support@mahstry.com</a></li>
                            <li>Support is provided during business hours (9 AM - 6 PM IST, Monday to Friday)</li>
                            <li>MahStry reserves the right to suspend or terminate accounts that violate our terms of service</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">9. Subscription & Renewal:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>Subscription will auto-renew unless cancelled before the renewal date</li>
                            <li>You can cancel your subscription at any time, but it will remain active until the end of the current billing period</li>
                            <li>No refunds will be provided for the remaining period of an active subscription upon cancellation</li>
                          </ul>
                        </div>

                        <div>
                          <p className="font-semibold mb-1">10. Acceptance & Agreement:</p>
                          <p className="ml-2">By checking the agreement box, you acknowledge that you have read, understood, and agree to be bound by all the terms and conditions stated above. You understand that violation of any of these terms may result in immediate account termination, legal action, and permanent ban from the platform.</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          id="agreedToTerms"
                          checked={formData.agreedToTerms}
                          onChange={(e) => handleInputChange('agreedToTerms', e.target.checked)}
                          className="mt-1 mr-3"
                        />
                        <label htmlFor="agreedToTerms" className="text-sm text-gray-700 dark:text-gray-300">
                          I agree to the terms and conditions stated above *
                        </label>
                      </div>
                      {errors.agreedToTerms && (
                        <p className="mt-1 text-sm text-red-600">{errors.agreedToTerms}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Subscription Plan */}
              {currentStep === 3 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                    Choose Subscription Plan
                  </h2>
                  
                  {!formData.category || !formData.studentRange ? (
                    <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle className="text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" size={20} />
                        <div>
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Please go back to Step 1 and select your category and student range
                          </p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                            Pricing is calculated based on these selections.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Batch Limit Information */}
                  {formData.category && formData.studentRange && (
                    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start">
                        <BookOpen className="text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" size={20} />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                            Included with Your Subscription:
                          </p>
                          <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                            <p>• <strong>{baseBatchLimit} batches</strong> included in base plan</p>
                            <p>• Need more? Purchase addon packs: <strong>₹{BATCH_ADDON_CONFIG.pricePerPack.toLocaleString()}/year</strong> for <strong>{BATCH_ADDON_CONFIG.batchesPerPack} batches</strong></p>
                            <p>• Maximum limit: <strong>{BATCH_ADDON_CONFIG.maxTotalBatches} batches</strong> (base + addons)</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {subscriptionPlans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`relative border-2 rounded-lg p-6 cursor-pointer transition-all ${
                          selectedPlan === plan.id
                            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-300 dark:border-gray-700 hover:border-primary-300'
                        } ${plan.popular ? 'ring-2 ring-primary-300' : ''}`}
                      >
                        {plan.popular && (
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                            Most Popular
                          </div>
                        )}
                        {/* Discount Badge */}
                        <div className="absolute -top-3 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                          {plan.discountPercent}% OFF
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          {plan.name}
                        </h3>
                        <div className="mb-2">
                          {/* Original Price (strikethrough) */}
                          <div className="text-sm text-gray-500 dark:text-gray-400 line-through mb-1">
                            ₹{plan.originalPrice.toLocaleString()}
                          </div>
                          {/* Discounted Price */}
                          <div>
                            <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                              ₹{plan.price.toLocaleString()}
                            </span>
                            <span className="text-gray-600 dark:text-gray-400">/{plan.period}</span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Includes 18% GST</p>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          {plan.description}
                        </p>
                        {plan.savings && (
                          <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
                            Save ₹{plan.savings.toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 dark:text-gray-300">Selected Plan:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {selectedPlanData.name}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm mb-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Base Amount:</span>
                        <span className="text-gray-900 dark:text-white">₹{(selectedPlanData.price / 1.18).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">CGST (9%):</span>
                        <span className="text-gray-900 dark:text-white">₹{((selectedPlanData.price / 1.18) * 0.09).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">SGST (9%):</span>
                        <span className="text-gray-900 dark:text-white">₹{((selectedPlanData.price / 1.18) * 0.09).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 dark:text-gray-300 font-semibold">Total Amount (incl. GST):</span>
                        <span className="text-2xl font-bold text-primary-600">
                          ₹{selectedPlanData.price.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      <p>Mahstry Private Limited</p>
                      <p>GSTIN: 08AAUCM5530E1Z9</p>
                    </div>
                  </div>

                  {/* Autopay Toggle */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <h3 className="font-semibold text-gray-900 dark:text-white">Enable Autopay</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Automatically renew your subscription at the end of each billing cycle. You can cancel anytime.
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={enableAutopay}
                          onChange={(e) => setEnableAutopay(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Educational Support Program */}
                  <EducationalSupportProgram />
                  
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                {currentStep < 3 ? (
                  <button
                    onClick={handleNext}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
                  >
                    Next
                    <ArrowRight className="ml-2" size={20} />
                  </button>
                ) : (
                  <button
                    onClick={handleSignup}
                    disabled={loading}
                    className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center disabled:opacity-50"
                  >
                    {loading ? 'Creating Account...' : 'Create Account & Pay'}
                    <CreditCard className="ml-2" size={20} />
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Sidebar - Features & Benefits */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Features */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Features You'll Get
                </h3>
                <div className="space-y-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <feature.icon className="text-primary-600" size={20} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                          {feature.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  Key Benefits
                </h3>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="text-green-500 mr-2 mt-1 flex-shrink-0" size={18} />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Payment Modal - One-time payment */}
      {showPaymentModal && (
        <SmartPaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setLoading(false);
          }}
          paymentData={{
            type: 'coaching_subscription',
            amount: subscriptionPlans.find(p => p.id === selectedPlan)?.price || 1000,
            currency: 'INR',
            description: `Coaching Subscription - ${subscriptionPlans.find(p => p.id === selectedPlan)?.name || 'Monthly Plan'}`,
            customerName: formData.contactPerson,
            customerEmail: formData.email,
            customerPhone: formData.phone,
            merchantName: 'Mahstry Private Limited',
            userId: auth.currentUser?.uid,
            metadata: {
              coachingName: formData.coachingName,
              plan: selectedPlan,
              planName: subscriptionPlans.find(p => p.id === selectedPlan)?.name,
              category: formData.category,
              studentRange: formData.studentRange,
              coachingId: `coaching_${Date.now()}`,
              contactPerson: formData.contactPerson,
              phone: formData.phone,
              address: {
                street: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode
              }
            }
          }}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
          title="Complete Subscription Payment"
        />
      )}

      {/* Subscription Modal - Autopay */}
      {showSubscriptionModal && (
        <SubscriptionPaymentModal
          isOpen={showSubscriptionModal}
          onClose={() => {
            setShowSubscriptionModal(false);
            setLoading(false);
          }}
          paymentData={{
            type: 'coaching_subscription',
            amount: subscriptionPlans.find(p => p.id === selectedPlan)?.price || 1000,
            currency: 'INR',
            description: `Coaching Subscription - ${subscriptionPlans.find(p => p.id === selectedPlan)?.name || 'Monthly Plan'}`,
            customerName: formData.contactPerson,
            customerEmail: formData.email,
            customerPhone: formData.phone,
            merchantName: 'Mahstry Private Limited',
            userId: auth.currentUser?.uid,
            metadata: {
              coachingName: formData.coachingName,
              plan: selectedPlan,
              planName: subscriptionPlans.find(p => p.id === selectedPlan)?.name,
              category: formData.category,
              studentRange: formData.studentRange,
              coachingId: `coaching_${Date.now()}`,
              contactPerson: formData.contactPerson,
              phone: formData.phone,
              address: {
                street: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode
              }
            }
          }}
          onSuccess={handleSubscriptionSuccess}
          onFailure={handlePaymentFailure}
          title="Activate Autopay Subscription"
        />
      )}

      {/* WhatsApp Contact Button */}
      <WhatsAppButton phoneNumber="7395007539" />
    </div>
    </>
  );
};

export default CoachingSignup;

